import { send, usePushSubscription } from 'server';
import { isArray, isNumber, isUndefined } from 'underscore';
import { useEffect, useRef } from 'react';
import { testParcel } from './testParcel';
import { anyPass, pipe, not } from 'ramda';

export const ParcelDetailedViewSubscribeRequest = 73000;
export const ParcelDetailedViewSubscribeResponse = 73001;
export const ParcelDetailedViewSubscribeErrorResponse = 73002;
export const ParcelDetailedViewNotification = 73006;
export const ParcelDiscoveryPageSubscribeRequest = 73010;
export const ParcelDiscoveryPageSubscribeResponse = 73011;
export const ParcelDiscoveryPageSubscribeErrorResponse = 73012;
export const ParcelJoinRequest = 73050;
export const ParcelJoinResponse = 73051;
export const ParcelJoinErrorResponse = 73052;
export const ParcelLeaveRequest = 73060;
export const ParcelLeaveResponse = 73061;
export const ParcelLeaveErrorResponse = 73062;
export const ParcelWidgetPossessionSubscribeRequest = 73030;
export const ParcelWidgetPossessionSubscribeResponse = 73032;
export const ParcelWidgetPossessionSubscribeErrorRequest = 73033;
export const ParcelWidgetPossessionNotification = 73036;

export const useDetailedView = (mysteryParcelId, updateParcel, setResponseError, lastWinnersSize) => {
    const parcelSetterRef = useRef();
    parcelSetterRef.current = updateParcel;
    const errorSetterRef = useRef();
    errorSetterRef.current = setResponseError;
    const update = ({ data }) => parcelSetterRef.current(getParcel(data));
    useEffect(() => {
        (mysteryParcelId === 'test'
                ? Promise.resolve(testParcel)
                : subscribeToDetailedView(mysteryParcelId, lastWinnersSize)
        ).then(
            update,
            error => errorSetterRef.current(error)
        );
    }, [mysteryParcelId, lastWinnersSize]);
    const preUpdate = (resp) => {
        if (mysteryParcelId === resp.data.mysteryParcelId) {
            update(resp);
        }
    };
    usePushSubscription(ParcelDetailedViewNotification, () => preUpdate);

/*    useEffect(() => {
        if (mysteryParcelId === 'test') {
            let { lastWinners } = testParcel.data.parcel;
            let timeoutId;
            const setTimeout = () => timeoutId = window.setTimeout(() => {
                setTimeout();
                lastWinners = [{ ...last(lastWinners), timeAwarded: Date.now() }, ...lastWinners.slice(0, -1)];
                update(mergeDeepRight(clone(testParcel), { data: { parcel: { lastWinners } } }));
            }, 500);
            setTimeout();
            return () => clearTimeout(timeoutId);
        }
    }, [mysteryParcelId]);*/
};

export const usePossessionInfo = (mysteryParcelId, updateParcel) => {
    const parcelSetterRef = useRef();
    parcelSetterRef.current = updateParcel;
    const update = ({ data }) => parcelSetterRef.current(getPossessionInfo(data));
    useEffect(() => {
        subscribeToPossessionInfo(mysteryParcelId).then(update);
    }, [mysteryParcelId]);
    usePushSubscription(ParcelWidgetPossessionNotification, () => update);
};

export const subscribeToDetailedView = (mysteryParcelId, lastWinnersSize) => send({
    ID: ParcelDetailedViewSubscribeRequest,
    responses: [
        ParcelDetailedViewSubscribeResponse,
        ParcelDetailedViewSubscribeErrorResponse
    ],
    description: 'Mystery Parcel Detailed View subscription',
    mysteryParcelId,
    lastWinnersSize
});

export const subscribeToPossessionInfo = mysteryParcelId => send({
    ID: ParcelWidgetPossessionSubscribeRequest,
    responses: [
        ParcelWidgetPossessionSubscribeResponse,
        ParcelWidgetPossessionSubscribeErrorRequest
    ],
    description: 'Mystery Parcel possession info subscription',
    mysteryParcelId
});

export const subscribeToDiscoveryPage = () => send({
    ID: ParcelDiscoveryPageSubscribeRequest,
    responses: [
        ParcelDiscoveryPageSubscribeResponse,
        ParcelDiscoveryPageSubscribeErrorResponse,
        10
    ],
    description: 'Mystery Parcel Discovery Page subscription'
});

export const join = mysteryParcelId => send({
    ID: ParcelJoinRequest,
    responses: [
        ParcelJoinResponse,
        ParcelJoinErrorResponse
    ],
    description: 'Mystery Parcel join request',
    mysteryParcelId
});

export const leave = mysteryParcelId => send({
    ID: ParcelLeaveRequest,
    responses: [
        ParcelLeaveResponse,
        ParcelLeaveErrorResponse
    ],
    description: 'Mystery Parcel leave request',
    mysteryParcelId
});

const asArray = value => isArray(value)
    ? value
    : isUndefined(value)
        ? []
        : [value];
const asSingleValue = value => isArray(value)
    ? value[0]
    : value;
const asUnixTimestamp = value => isNumber(value)
    ? value < 30000000000
        ? value * 1000
        : value
    : undefined;

const findParcel = (value = {}) => value.mysteryParcelId !== undefined
    ? value
    : value.parcel || value.parcels;
const findPossessionInfo = (value = {}) => value;
export const getParcel = value => asSingleValue(findParcel(value));
export const getPossessionInfo = value => asSingleValue(findPossessionInfo(value));

export const isNotStartedStatus = status => status === 'NOT_STARTED';
export const isActiveStatus = status => status === 'ACTIVE';
export const isIdleStatus = status => status === 'IDLE';

export const isNotStarted = isNotStartedStatus;
export const isActive = anyPass([isActiveStatus, isIdleStatus]);
export const isCompleted = pipe(anyPass([isNotStarted, isActive]), not);

export const getMysteryParcelId = ({ mysteryParcelId } = {}) => mysteryParcelId;
export const getJoined = ({ joined } = {}) => Boolean(joined);
export const getEligible = ({ eligibilityRules } = {}) =>
    asArray(eligibilityRules).reduce((acc, { eligibilityFulfilled, eligbilityFulfilled } = {}) =>
        acc && (eligibilityFulfilled || eligbilityFulfilled), true);
export const getPrizes = ({ prizes } = {}) =>
    asArray(prizes).map(({ displayName, awarded, remaining, finalPrize = false }) => ({
        displayName,
        awarded,
        remaining,
        finalPrize
    }));
export const getPrizesWon = ({ prizesWon } = {}) =>
    asArray(prizesWon).map(({ prizeDisplayName, awarded, finalPrize = false }) => ({
        prizeDisplayName,
        awarded,
        finalPrize
    }));
export const getLastWinners = ({ lastWinners } = {}) =>
    asArray(lastWinners).map(({ name, nickname, prizeDisplayName, timeAwarded, finalPrize = false,
                                  isCurrentUser = false }) => ({
        name,
        nickname,
        prizeDisplayName,
        timeAwarded: asUnixTimestamp(timeAwarded),
        finalPrize,
        isCurrentUser
    }));
export const getParticipatingGames = ({ participatingGames } = {}) => asArray(participatingGames);
export const getRules = ({ rules } = {}) => rules;
export const getStatus = ({ status } = {}) => status;
export const getEventEndTime = ({ eventEndTime } = {}) => asUnixTimestamp(eventEndTime);
export const getEventStartTime = ({ eventStartTime } = {}) => asUnixTimestamp(eventStartTime);
export const getDisplayName = ({ displayName } = {}) => displayName;
export const getRegistrationStatus = ({ registrationStatus } = {}) => registrationStatus;
export const getTermsAndConditionsUrl = ({ termsAndConditionsUrl } = {}) => termsAndConditionsUrl;
export const getPossessionDataInfo = ({ data } = {}) => data;
export const getBannerUrl = ({ bannerUrl } = {}) => bannerUrl;
export const getCustomAttributes = ({ customAttributes } = {}) => customAttributes;
